import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { signIn, sendOtp, verifyOtp, resetPassword } from "../../webServices/Apis";
import Loader from "../Loader/Loader";
import { message } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { UserContext } from "../../App";
import backGround from "../../images/ball-7280265_1280.jpg";
import logo from "../../images/launch_axcell.png";
import { baseUrl } from "../../webServices/env";


const Login = () => {
  const { setIsAuthenticated } = useContext(UserContext);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [redirect, setRedirect] = useState(0)
  const [timer, setTimer] = useState(60); // Initial 60 seconds



  // Listen for back button and redirect to /home
  useEffect(() => {
    const handlePopState = () => {
      navigate('/login'); // Redirect to /login when the back button is pressed
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);


  // Start the timer when the component is mounted or Resend OTP is clicked
  useEffect(() => {
    if (redirect === 2) {
      if (timer > 0) {
        const interval = setInterval(() => {
          setTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval); // Cleanup interval on unmount
      }
    }

  }, [redirect, timer]);


  const handleForgetPassword = () => {
    setRedirect(1);
    return;
  }


  const initialValues = {
    email: "",
    password: "",
    otpEmail: "",
    otp: ['', '', '', ''],  // 4 OTP digits as an array
    newPassword: "",
    confirmPassword: ""
  };

  const getValidationSchema = (redirect) => {

    switch (redirect) {

      case 0: // Sign In
        return Yup.object({
          email: Yup.string()
            .email("Invalid email address")
            .matches(
              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              "Enter valid email"
            )
            .required("Email id is required"),

          password: Yup.string().required("Password is required"),
        })

      case 1: // Send Otp
        return Yup.object({
          otpEmail: Yup.string()
            .email("Invalid email address")
            .matches(
              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              "Enter valid email"
            )
            .required("Email id is required"),
        })

      case 2: // Verify Otp
        return Yup.object().shape({
          otp: Yup.array()
          .of(Yup.string().matches(/^\d$/, "Otp digits should be a number")) // Ensures only numbers
          .test("otp-length", "OTP is required", (otp) => otp?.filter(Boolean).length === 4) // Ensures exactly 4 filled digits
        })

      case 3: // Reset Password
        return Yup.object({
          newPassword: Yup.string()
            .matches(
              /^(?=(.*[a-z]))(?=(.*[A-Z]))(?=(.*\d))(?=(.*[\W_])).{8,}$/,
              "Password must be at least 8 characters, include an uppercase letter, a lowercase letter, a number, and a special character."
            )
            .required("Password is required"),

          confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], "New password and confirm password should be match") // Ensures confirmPassword matches newPassword
            .required("Confirm password is required")
        })
    }
  }

  const handleResendOtp = async () => {
    try {
      setLoading(true)
      const response = await sendOtp(formik.values.otpEmail)
      if (response?.data?.success) {
        message.success(response?.data?.message)
        // Clear only the OTP field
        formik.setFieldValue('otp', ['', '', '', '']);
        setTimer(60);
      }

    } catch (error) {
      console.log("Resend Otp Error ", error.message)
      message.error(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }


  const handleSubmit = async (values, { setSubmitting, resetForm }, actionType) => {
    let response;
    setLoading(true)
    setSubmitting(true)
    try {
      // Check the actionType and call the appropriate function
      if (actionType === 'signIn') {
        response = await signIn(values);
        if (response?.data?.success === true) {

          localStorage.setItem("token", response?.data?.data?.token);
          localStorage.setItem("qbKey", response?.data?.data?.qbEnabled);

          if (response?.data?.data?.qbEnabled === true) {
            const adminId = response?.data?.data?.id
            message.success("Redirecting to Quick Books");
            window.location.href = `${baseUrl}/api/quickbooks/authorize?admin_id=${adminId}`;

          }
          else {
            message.success(response?.data?.message);
            setTimeout(() => {
              window.location.href = "/"
            }, 100);
          }
        }
      } 
      
      if (actionType === 'sendOtp') {
        const otpEmail = values.otpEmail;
        response = await sendOtp(otpEmail);
        if (response?.data?.success) {
          message.success(response?.data?.message);
          setRedirect(2);
        }
      } 
      
      
      if (actionType === 'verifyOtp') {
        response = await verifyOtp({ values });
        if (response?.data?.success) {
          message.success(response?.data?.message);
          formik.setFieldValue('otp', ['', '', '', '']);
          setRedirect(3);
        }

      }

      if (actionType === 'resetPassword') {
        response = await resetPassword({ values });
        if (response?.data?.success) {
          message.success(response?.data?.message);
          setRedirect(0);
        }
      }

    } catch (error) {
      console.log("Error during submission:", error);
      if (error?.response?.status === 500) {
        message.error("Server not responding");
      } else {
        message.error(error?.response?.data?.message || "Error during submission");
      }
    } finally {
      // Always setSubmitting(false) in the finally block to ensure it is called regardless of success or failure
      setSubmitting(false);
      setLoading(false);
    }
  };


  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema(redirect),

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      // Join OTP digits to form the full OTP string
      const otp = values.otp.join('') || "";   // This joins the OTP array into a single string
      // Check redirect state and send appropriate actionType
      if (redirect === 0) {
        await handleSubmit(values, { setSubmitting, resetForm }, 'signIn')
      }
      else if (redirect === 1) {
        await handleSubmit(values, { setSubmitting, resetForm }, 'sendOtp')
      } else if (redirect === 2) {
        await handleSubmit({ ...values, otp }, { setSubmitting, resetForm }, 'verifyOtp');
      } else if (redirect === 3) {
        await handleSubmit(values, { setSubmitting, resetForm }, 'resetPassword')
      }
    }
  });


  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),

  ];


  const handleBackspace = (index, event) => {
    if (event.key === 'Backspace' && index > 0 && !event.target.value) {
      inputRefs[index - 1].current.focus(); // Move focus to the previous input field
    }
  };

  const handleDigitInput = (index, value) => {
    // Update Formik's OTP value
    const otp = [...formik.values.otp];
    otp[index] = value;    // Update the specific digit in the OTP array
    formik.setFieldValue('otp', otp);  // Set the OTP array in Formik

    // Move focus to the next input field
    if (value.length > 0 && index < inputRefs.length - 1) {
      inputRefs[index + 1]?.current?.focus();
    }

    // Move focus to the previous input field if the value is deleted
    else if (value.length === 0 && index > 0) {
      inputRefs[index - 1]?.current?.focus();
    }
  };


  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url(${backGround})`, // Replace this URL with your actual background image
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >



      {redirect === 0 && (

        <div className="bg-white bg-opacity-90 p-10 rounded-xl shadow-2xl w-full max-w-md">
          <h2 className="text-3xl font-extrabold mb-8 text-center text-gray-800">
            Login
          </h2>
          <div className="h-20 flex justify-center mt-4">
            <img className="h-20" src={logo} alt="logo" />
          </div>
          <h3 className="text-2xl font-extrabold mb-8 text-center text-gray-800">Axcell Painting Admin</h3>
          {loading && <Loader />}

          <form onSubmit={formik.handleSubmit}>
            <div className="mb-6">
              <label className="block mb-3 text-gray-800 font-semibold" htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`border-2 p-3 w-full rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 transition ${formik.errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-600 text-sm mt-2">{formik.errors.email}</p>
              )}
            </div>

            <div className="mb-6 relative">
              <label className="block mb-3 text-gray-800 font-semibold" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`border-2 p-3 w-full rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 transition ${formik.errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center "
                >
                  <FontAwesomeIcon
                    icon={showPassword ? faEye : faEyeSlash}
                    className="text-gray-500 mr-2"
                  />
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <p className="text-red-600 text-sm mt-2">{formik.errors.password}</p>
              )}
            </div>

            <div className=" text-blue-600 w-40 mt-1 mb-4 " >
              <a variant="small" color="blue-gray" >
                <button type='button' className="font-medium hover:text-orange-900 underline" onClick={handleForgetPassword} style={{ cursor: "pointer" }}> Forgot Password</button>
              </a>
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r mt- from-blue-500 to-blue-600 text-white font-semibold py-3 w-full rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
            >
              {formik.isSubmitting ? 'Loging in ...' : 'Login'}
            </button>

          </form>
        </div>
      )}


      {redirect === 1 && (

        <div className="bg-white bg-opacity-90 p-10 rounded-xl shadow-2xl w-full max-w-md">
          <div className="h-20 flex justify-center mt-4">
            <img className="h-20" src={logo} alt="logo" />
          </div>
          <h3 className="text-2xl font-extrabold mb-8 text-center text-gray-800">Axcell Painting Admin</h3>

          <h3 className='font-bold mt-10 mb-5 text-center'>Enter your registered email</h3>

          {loading && <Loader />}
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-6">
              <label className="block mb-3 text-gray-800 font-semibold" htmlFor="otpEmail">
                Email
              </label>
              <input
                type="otpEmail"
                id="otpEmail"
                name="otpEmail"
                placeholder="Enter your email"
                value={formik.values.otpEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`border-2 p-3 w-full rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 transition ${formik.errors.otpEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formik.touched.otpEmail && formik.errors.otpEmail && (
                <p className="text-red-600 text-sm mt-2">{formik.errors.otpEmail}</p>
              )}
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r mt- from-blue-500 to-blue-600 text-white font-semibold py-3 w-full rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
            >
              {formik.isSubmitting ? 'Sending ...' : 'Send OTP'}
            </button>
          </form>
        </div>

      )}



      {redirect === 2 && (

        <div className="bg-white bg-opacity-90 p-10 rounded-xl shadow-2xl w-full max-w-md">
          <div className="h-20 flex justify-center mt-4">
            <img className="h-20" src={logo} alt="logo" />
          </div>
          <h3 className="text-2xl font-extrabold mb-8 text-center text-gray-800">Axcell Painting Admin</h3>

          <h3 className='font-bold mt-10 mb-5 text-center'>Enter your 4 digit otp to verify your email</h3>

          {loading && <Loader />}
          <form onSubmit={formik.handleSubmit}>
            <div className="flex justify-center gap-8">

              <div className="space-x-2 mt-4 mb-4">
                {formik.values.otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleDigitInput(index, e.target.value)}
                    onKeyDown={(e) => handleBackspace(index, e)}
                    ref={inputRefs[index]}
                    className={`w-10 h-10 text-center border rounded-lg focus:outline-none transition ${
                      formik.errors.otp ? "border-red-500" : "border-black"
                    }`}
                  />
                ))}
                {/* Show error message */}
              {formik.errors.otp && (
                <p className="text-red-600 text-sm mt-2 text-center">{formik.errors.otp}</p>
              )}
              </div>
              
            </div>

            <div className=" text-blue-600 w-40 mb-2 " >
              <a variant="small" color="blue-gray" >
                <button type='button' className="font-medium hover:text-orange-900 underline"
                  onClick={handleResendOtp}
                  style={{ cursor: timer > 0 ? "not-allowed" : "pointer" }}
                  disabled={timer > 0}
                >
                  {timer > 0 ? `Resend in : ${timer} s` : "Resend OTP"}
                </button>
              </a>
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r mt- from-blue-500 to-blue-600 text-white font-semibold py-3 w-full rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
            >
              {formik.isSubmitting ? 'Verifying ...' : 'Verify OTP'}
            </button>
          </form>
        </div>
      )}



      {redirect === 3 && (

        <div className="bg-white bg-opacity-90 p-10 rounded-xl shadow-2xl w-full max-w-md">
          <div className="h-20 flex justify-center mt-4">
            <img className="h-20" src={logo} alt="logo" />
          </div>
          <h3 className="text-2xl font-extrabold mb-8 text-center text-gray-800">Axcell Painting Admin</h3>

          <h3 className='font-bold mt-10 mb-5 text-center'>Reset your password</h3>

          {loading && <Loader />}
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-6">
              <label className="block mb-3 text-gray-800 font-semibold" htmlFor="newPassword">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  placeholder="Enter your new password"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`border-2 p-3 w-full rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 transition ${formik.errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 flex items-center "
                >
                  <FontAwesomeIcon
                    icon={showNewPassword ? faEye : faEyeSlash}
                    className="text-gray-500 mr-2"
                  />
                </button>
              </div>
              {formik.touched.newPassword && formik.errors.newPassword && (
                <p className="text-red-600 text-sm mt-2">{formik.errors.newPassword}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block mb-3 text-gray-800 font-semibold" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`border-2 p-3 w-full rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 transition ${formik.errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                />

                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center "
                >
                  <FontAwesomeIcon
                    icon={showConfirmPassword ? faEye : faEyeSlash}
                    className="text-gray-500 mr-2"
                  />
                </button>
              </div>
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-2">{formik.errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r mt- from-blue-500 to-blue-600 text-white font-semibold py-3 w-full rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
            >
              {formik.isSubmitting ? 'Reseting ...' : 'Reset Password'}
            </button>
          </form>
        </div>
      )}


    </div>
  );
};

export default Login;
